import FormStep from "../../../services/models/formModels/MultiStepForm/formStep.model";
import CustomText from "../../atomic/CustomText";
import CustomRadioInput from "../../molecular/CustomRadioInput";
import CustomCheckBoxInput from "../../molecular/CustomCheckboxInput";
import WarningBanner from "../WarningBanner";
import {
  SCC_ADDITIONAL_HISTORY_OPTIONS,
  SCC_ADDITIONAL_PATHOLOGY_OPTIONS,
  SCC_AGGRESSIVE_SUBTYPE_FOOTNOTE,
  SCC_LOW_RISK_DISCLAIMER,
  SCC_REQUIRED_HISTORY_QUESTIONS,
  SCC_REQUIRED_PATHOLOGY_QUESTIONS,
} from "../../../constants/sccClinicalInfoConfig";
import { isSccIntegratedTestBlocked } from "../../../utils/helpers/sccEligibility";

function SCCClinicalInformationStep({
  values,
  setFieldValue,
  setFieldTouched,
  getFieldMeta,
  fieldContainerHeading,
  errors,
  touched,
}: Readonly<FormStep>): JSX.Element {
  const blocked = isSccIntegratedTestBlocked(values);

  return (
    <div
      className={`bg-white p-6 shadow-md rounded-md border ${
        blocked ? "border-requiredRed" : "hover:border-primary border"
      }`}
    >
      {blocked && (
        <WarningBanner warning={SCC_LOW_RISK_DISCLAIMER} className="mb-6" />
      )}
      {fieldContainerHeading && (
        <CustomText
          fontWeight="font-semibold"
          text={fieldContainerHeading}
          className="my-2"
          fontSize="font-xl"
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
        <div className="flex flex-col gap-4">
          <CustomText
            text="REQUIRED SECTION"
            fontWeight="font-bold"
            className="text-royalBlue uppercase text-sm"
          />
          <CustomText
            text="*MISSING RESPONSES WILL DELAY TEST PROCESSING*"
            fontWeight="font-semibold"
            className="text-royalBlue italic underline text-sm"
          />

          <CustomText
            text="History and Physical Exam:"
            fontWeight="font-semibold"
            className="text-royalBlue mt-2"
          />
          {SCC_REQUIRED_HISTORY_QUESTIONS.map((question) => (
            <CustomRadioInput
              key={question.name}
              labelInfo={{
                label: question.label,
                inputId: question.name,
                required: true,
              }}
              name={question.name}
              options={[...question.options]}
              values={values}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              manualFieldsChangeOnOptionClick={(
                _option,
                _setFieldValue,
                setFieldTouchedFn
              ) => {
                setFieldTouchedFn?.(question.name, true);
                return {};
              }}
            />
          ))}

          <CustomText
            text="Surgical and Pathological Findings:"
            fontWeight="font-semibold"
            className="text-royalBlue mt-4"
          />
          {SCC_REQUIRED_PATHOLOGY_QUESTIONS.map((question) => (
            <CustomRadioInput
              key={question.name}
              labelInfo={{
                label: question.label,
                inputId: question.name,
                required: true,
              }}
              name={question.name}
              options={[...question.options]}
              values={values}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              manualFieldsChangeOnOptionClick={(
                _option,
                _setFieldValue,
                setFieldTouchedFn
              ) => {
                setFieldTouchedFn?.(question.name, true);
                return {};
              }}
            />
          ))}
        </div>

        <div className="flex flex-col gap-4">
          <CustomText
            text="CHECK ANY ADDITIONAL HIGH RISK FEATURES:"
            fontWeight="font-bold"
            className="text-royalBlue uppercase text-sm"
          />

          <CustomCheckBoxInput
            labelInfo={{
              label: "History and Physical Exam:",
              inputId: "historyPhysicalExam.additionalHistoryPhysicalExam",
              required: false,
            }}
            name="historyPhysicalExam.additionalHistoryPhysicalExam"
            checkBoxes={SCC_ADDITIONAL_HISTORY_OPTIONS}
            values={values}
            errors={errors}
            touched={touched}
            getFieldMeta={getFieldMeta}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            manualFieldsChangeOnOptionClick={(
              _checked,
              _setFieldValue,
              setFieldTouchedFn
            ) => {
              setFieldTouchedFn?.(
                "historyPhysicalExam.additionalHistoryPhysicalExam",
                true
              );
            }}
          />

          <CustomCheckBoxInput
            labelInfo={{
              label: "Surgical and Pathological Findings:",
              inputId:
                "pathologySurgicalFindings.additionalPathologySurgicalFindings",
              required: false,
            }}
            name="pathologySurgicalFindings.additionalPathologySurgicalFindings"
            checkBoxes={SCC_ADDITIONAL_PATHOLOGY_OPTIONS}
            values={values}
            errors={errors}
            touched={touched}
            getFieldMeta={getFieldMeta}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            parentContainerClass="mt-2"
            manualFieldsChangeOnOptionClick={(
              _checked,
              _setFieldValue,
              setFieldTouchedFn
            ) => {
              setFieldTouchedFn?.(
                "pathologySurgicalFindings.additionalPathologySurgicalFindings",
                true
              );
            }}
          />

          <CustomText
            text={SCC_AGGRESSIVE_SUBTYPE_FOOTNOTE}
            fontWeight="font-normal"
            className="text-xs text-darkGray mt-2"
          />
        </div>
      </div>
    </div>
  );
}

export default SCCClinicalInformationStep;
